import { Fragment, useState } from "react";
import cn from "classnames";
import download from "downloadjs";

import { Caption } from "@ascendnow/shared";

import AscGenericCard from "../AscCards/AscGenericCard/AscGenericCard";
import AscButton from "../AscButton/AscButton";
import { DocReader } from "./DocReader";
import AscTooltip from "../AscTooltip/AscTooltip";
import { AscResourcePurchaseModal } from "./AscResourcePurchaseModal";
import { ReactComponent as LikeIcon } from "../../assets/icons/like.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import styles from "./AscResourceDocCard.module.scss";

export interface IAscResourceDocCardProps {
  title: string;
  description: string;
  documentType?: string;
  downloadLink: string;
  fileName?: string;
  isFavorite?: boolean;
  thumbnail: string;
  className?: string;
  isUnlocked?: boolean;
  docTitle?: string;
  captions?: Caption[];
  cost: number;
  handleFavBtn: () => void;
  unlockResource: (cb: () => void) => void;
}

export default function AscResourceDocCard({
  title,
  description,
  documentType,
  downloadLink,
  fileName,
  isFavorite,
  thumbnail,
  className,
  handleFavBtn,
  isUnlocked,
  cost,
  unlockResource,
  docTitle,
  captions,
}: IAscResourceDocCardProps) {
  const [openFileReader, setFileReader] = useState(false);
  const [purchaseSrc, setPurchaseSrc] = useState(false);

  const handleDownload = async () => {
    try {
      if (downloadLink) {
        const response = await fetch(downloadLink);
        if (response.ok) {
          const blob = await response.blob();
          download(blob, fileName || docTitle || "download");
        } else {
          console.error("Error during download:", response.statusText);
        }
      }
    } catch (error) {
      console.error("Error during download:", error);
    }
  };

  return (
    <Fragment>
      {purchaseSrc && (
        <AscResourcePurchaseModal
          {...{
            visible: purchaseSrc,
            setVisible: setPurchaseSrc,
            cost,
            unlockResource,
          }}
        />
      )}
      {openFileReader && (
        <DocReader
          {...{
            visible: openFileReader,
            setVisible: setFileReader,
            src: downloadLink,
            captions:
              captions && captions.length > 0 ? captions[0]?.translations : [],
          }}
        />
      )}
      <AscGenericCard
        data-testid="asc-resource-doc-card"
        className={styles.card}
        coverImageUrl={thumbnail}
        title={
          <div className={styles.cardTitleWrapper}>
            <h5 className={styles.cardTitle}>{title}</h5>
            <LikeIcon
              onClick={handleFavBtn}
              className={isFavorite ? styles.favorite : ""}
            />
          </div>
        }
        footer={
          <div
            className={cn(
              styles.cardFooter,
              !isUnlocked && styles["cardFooter--paid"]
            )}
          >
            <AscButton
              variant="secondary"
              className={styles.cardBtn}
              onClick={() => setFileReader(true)}
              disabled={!downloadLink}
            >
              View
            </AscButton>
            {isUnlocked || cost < 1 ? (
              <AscButton
                variant="primary"
                disabled={!downloadLink}
                className={styles.cardDownloadBtn}
                onClick={handleDownload}
              >
                Download
              </AscButton>
            ) : (
              <AscButton
                className={styles.cardPurchaseBtn}
                onClick={() => setPurchaseSrc(true)}
              >
                <LockIcon /> Unlock for {cost.toFixed()} coins
              </AscButton>
            )}
          </div>
        }
        contentClassName={styles.cardContent}
      >
        {description && description.length > 60 ? (
          <AscTooltip
            tooltipAnchor={
              <span
                className={styles.cardDescriptionShort}
                dangerouslySetInnerHTML={{
                  __html: description?.slice(0, 60) + "...",
                }}
              />
            }
            events="click"
            tooltipContentClassName={styles.tooltipContent}
          >
            <span
              className={styles.cardDescription}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </AscTooltip>
        ) : (
          <span
            className={styles.cardDescription}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        {documentType && (
          <span className={styles.cardDocumentType}>
            {documentType?.split(".").pop()?.toLocaleUpperCase()}
          </span>
        )}
      </AscGenericCard>
    </Fragment>
  );
}
